import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { KAL_DIALOG_DATA, KalDate, KalDialogRef } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import get from 'lodash-es/get';
import head from 'lodash-es/head';
import startsWith from 'lodash-es/startsWith';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    generateDistributionKeys
} from 'src/app/_shared/components/analytic-plan-distributer/analytical-plan-distributer.component';
import {
    InitAnalyticalDistributionAction,
    ResetAnalyticalPlanStateAction
} from 'src/app/_shared/stores/analytical-plan/analytical-plan.actions';
import {
    SharedPaymentMethodsState
} from 'src/app/_shared/stores/settlements/shared-payment-methods/shared-payment-methods.state';
import { UserState } from 'src/app/_user/store/user/user.state';
import { accountingMovementDirection, MoyensPaiement, TypeJournal } from 'src/app/board/activity/activity.constants';
import { ActivitySalesState } from 'src/app/board/activity/stores/activity-sales/activity-sales.state';
import { getISODate } from 'src/app/utils/date.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { environment } from 'src/environments/environment';
import {
    ActiviteLightModel,
    BenefDto,
    CompteBancaireDto,
    CompteLightModel,
    ContextInfosInput,
    EcritureGenerationEcritureReglementModelInput,
    FactureDto,
    JournalLightModel,
    MoyensPaiementMoyenPaiementLightModel,
    MutationGetIdCompteReglementFiduciaireArgs,
    MutationGetListeActivitesLightArgs,
    ReglementsReglementModelInput,
} from 'src/graphql/generated-types';
import { BeneficiaryPipe } from 'src/app/_shared/pipes/beneficiary.pipe';
import { arrayOnlyOne } from 'src/app/utils/array.utils';
import {
    AccountingWritingClassType
} from 'src/app/_shared/stores/settlements/shared-advanced-settlement/shared-advanced-settlement.xml';

export interface SharedOverpaymentDialogData {

    accepted?: boolean;

    // Infos à fournir
    beneficiary: BenefDto;

    overpaymentAmount: number;

    latestInvoiceDate: KalDate;

    invoices: FactureDto[];

    // Infos à renvoyer
    overpaymentAccountId?: number;

    payment?: ReglementsReglementModelInput;

}

@Component({
    selector: 'app-shared-overpayment-dialog',
    templateUrl: './shared-overpayment-dialog.component.html',
    styleUrls: ['./shared-overpayment-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedOverpaymentDialogComponent extends BaseComponent implements OnInit {

    form: UntypedFormGroup;

    paymentMethodsList: MoyensPaiementMoyenPaiementLightModel[] = [];

    accountsList: CompteLightModel[] = [];

    journalsList: JournalLightModel[] = [];

    bankAccountsList: CompteBancaireDto[] = [];

    collectivePMVAccountsList: CompteLightModel[] = [];

    overpaymentAccountsList: CompteLightModel[] = [];

    bankList: string[] = [];
    
    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly fb: UntypedFormBuilder,
                private readonly graphqlService: GraphqlService,
                private readonly dialogRef: KalDialogRef<SharedOverpaymentDialogComponent>,
                @Inject(KAL_DIALOG_DATA) public data: SharedOverpaymentDialogData) {
        super(cdr);
    }

    get overpaymentAmount(): number {
        return this.data.overpaymentAmount;
    }

    get isRefund(): boolean {
        return this.form.value.policy === 'refund';
    }

    get selectedPaymentMethod(): MoyensPaiementMoyenPaiementLightModel {
        return this.paymentMethodsList.find(m => m.Id === +this.form.get('refund.paymentMethodId').value, null);
    }

    get accounts(): CompteLightModel[] {
        if (this.form.get('extraordinaryProduct.displayAllAccounts').value) {
            return this.accountsList;
        }
        return this.accountsList.filter(account => startsWith(account.Numero, '7'));
    }

    get refundJournals(): JournalLightModel[] {
        const payementMethod = this.selectedPaymentMethod;
        if (!payementMethod) {
            return [];
        }
        const filteredJournalList = (journaltype: string): JournalLightModel[] => {
            return this.journalsList.filter(j => j.TypeJournal === journaltype);
        };

        if (!payementMethod.IsFiduciaire && payementMethod.CodeType !== MoyensPaiement.PorteMonnaieVirtuel) {
            return filteredJournalList(TypeJournal.Tresorerie);
        }
        if (payementMethod.CodeType === MoyensPaiement.PorteMonnaieVirtuel) {
            return filteredJournalList(TypeJournal.OuvrantDroit);
        }
        if (payementMethod.IsFiduciaire) {
            return filteredJournalList(TypeJournal.EspecesEtAutres);
        }
    }

    get productJournals(): JournalLightModel[] {
        return this.journalsList.filter(j => j.TypeJournal === TypeJournal.OuvrantDroit && j.TypeBudget === 'S');
    }

    get shouldDisplayBankAccount(): boolean {
        const paymentMethod = this.selectedPaymentMethod;
        if (!paymentMethod) {
            return false;
        }
        return !paymentMethod.IsFiduciaire && paymentMethod.CodeType !== MoyensPaiement.PorteMonnaieVirtuel;
    }

    get shouldDisplayCheque(): boolean {
        const paymentMethod = this.selectedPaymentMethod;
        return !paymentMethod ? false : (paymentMethod.CodeType === MoyensPaiement.ChequeBancaire);
    }

    get shouldDisplayPmvAccount(): boolean {
        const paymentMethod = this.selectedPaymentMethod;
        return !paymentMethod ? false : (paymentMethod.CodeType === MoyensPaiement.PorteMonnaieVirtuel);
    }

    private get context(): ContextInfosInput {
        return this.snapshot(UserState.userContext);
    }

    private get beneficiaryId(): number {
        return this.data?.beneficiary?.Id;
    }

    initForm(): void {
        this.form = this.fb.group({
            overpaymentBankAccount: ['', [FormValidator.isNotNull]],
            policy: ['extraordinaryProduct', [FormValidator.isNotNull]],
            refund: this.fb.group({
                refundDate: [new KalDate(), [FormValidator.minDateValidator(this.data.latestInvoiceDate)]],
                paymentMethodId: ['', [FormValidator.isNotNull]],
                bankAccountId: ['', [FormValidator.isNotNull]],

                // cheque
                chequeNumber: [''], // Les validateurs seront mis à jour au changement de moyen de paiement
                bankName: [''],
                tiretName: ['', [Validators.maxLength(50)]],

                // PMV
                collectiveAccountId: ['', [FormValidator.isNotNull]],
                journalRefund: [null, [FormValidator.isNotNull]]
            }),
            extraordinaryProduct: this.fb.group({
                account: [null, [FormValidator.isNotNull]],
                displayAllAccounts: [false],
                journalExtra: [null, [FormValidator.isNotNull]],
                distribution: [null]
            }),
        });

        // En fonction du choix remboursement ou produit extraordinaire on désactive le formulaire non concerné (et réactive l'autre)
        this.watch$ = this.form.get('policy').valueChanges.pipe(
            startWith(this.form.value.policy),
            tap((value: string) => {
                const fieldToDisable = value !== 'refund' ? 'refund' : 'extraordinaryProduct';

                this.form.get(value).enable({emitEvent: false});
                this.form.get(fieldToDisable).disable({emitEvent: false});

                this.cdr.markForCheck();
            })
        );

        this.watch$ = this.form.get(['refund', 'bankAccountId']).valueChanges.pipe(
            map((bankAccountId) => this.getJournalIdFromBankAccountBudget(bankAccountId)),
            filter(loneJournalId => !!loneJournalId),
            tap((loneJournalId) => {
                this.form.get(['refund', 'journalRefund']).patchValue(loneJournalId);
            })
        );

        this.initRefundFormInteractions();
    }

    submit(): void {
        this.watch$ = this.generatePayment().pipe(
            tap(payment => {
                this.data.payment = payment;
                this.data.overpaymentAccountId = this.form.get('overpaymentBankAccount').value;

                this.data.accepted = true;

                this.dispatch$(new ResetAnalyticalPlanStateAction());
                this.dialogRef.close(this.data);
            })
        );
    }

    close(): void {
        this.dispatch$(new ResetAnalyticalPlanStateAction());
        this.dialogRef.close();
    }

    /**
     * Fonction initialisant les observables sur le sous formulaire refund
     */
    private initRefundFormInteractions(): void {
        type disableEnableType = 'enable' | 'disable';
        const toEnable = (value: boolean): disableEnableType => value ? 'enable' : 'disable';

        // Action à effectuer lors du changement de méthode de paiement
        this.watch$ = this.form.get('refund.paymentMethodId').valueChanges.pipe(
            tap(() => {
                // En fonction du moyen de paiement on marque les controles à activer/désactiver
                const disableEnable: { [controlName: string]: disableEnableType } = {
                    collectiveAccountId: toEnable(this.shouldDisplayPmvAccount),
                    chequeNumber: toEnable(this.shouldDisplayCheque),
                    bankName: toEnable(this.shouldDisplayCheque),
                    tiretName: toEnable(this.shouldDisplayCheque),
                    bankAccountId: toEnable(this.shouldDisplayBankAccount)
                };

                // On active/désactive chaque controles
                Object.keys(disableEnable).forEach(key => this.form.get('refund.' + key)[disableEnable[key]]());

                this.updateChequesValidators();
                this.cdr.markForCheck();
            })
        );
    }

    /**
     * Mets à jour les validateurs du Numéro de chèque
     */
    private updateChequesValidators(): void {
        const control = this.form.get('refund.chequeNumber');
        control.clearValidators();

        if (!this.shouldDisplayCheque) {
            return;
        }

        const checkNumbersValidators = [Validators.maxLength(20)];
        const requiredCheckNumber = this.snapshot(UserState.applicationParameters)
            .find(p => p.CodeChamp === 'CNUMCH')?.CodeValeur === '1';
        if (requiredCheckNumber) {
            checkNumbersValidators.push(FormValidator.isNotNull);
        }

        control.setValidators(checkNumbersValidators);
        control.updateValueAndValidity();
    }

    /**
     * Appelle la mutation InitialiserPopupTropPercu
     */
    private initOverpaymentInfos(): void {
        this.loading = true;
        type InitializationOverpayment = 'InitialiserPopupTropPercu' | 'GetListeCompteBancaireGenerique';
        this.watch$ = this.activityIdsList$().pipe(
            switchMap((activitiesList) =>  this.graphqlService.mutateMultiple<InitializationOverpayment>({
                mutation: gql`
                    mutation InitialiserPopupTropPercu($contexte: ContextInfosInput, $listeIdsActivite: ArrayOfdecimalInput) {
                        InitialiserPopupTropPercu(contexte: $contexte, listeIdsActivite: $listeIdsActivite) {
                            ListeMoyensPaiement {
                                MoyensPaiementMoyenPaiementLightModel {
                                    CodeType
                                    Id
                                    IsFiduciaire
                                    Libelle
                                }
                            }
                            ListeComptes {
                                ... lightAccounts
                            }
                            ListeJournaux {
                                JournalLightModel {
                                    Id
                                    Libelle
                                    TypeBudget
                                    TypeJournal
                                }
                            }
                            ListeComptesCollectifPMV {
                                ... lightAccounts
                            }
                            ListeComptesCollectifClient {
                                ... lightAccounts
                            }
                            ListeBanques {
                                string
                            }
                            IdSectionAnalytique
                            IdCompteBancaireActivite
                            IdCompteTropPercu
                            IdCollectifTropPercu,
                        },
                        GetListeCompteBancaireGenerique(
                            contexte: $contexte
                            critere: {
                                IsClos: false
                                TakeCompte512: true
                            }
                        ) {
                            CompteBancaireDTO {
                                Id
                                Libelle
                                IsSocial
                                IsFonctionnement
                                Compte {
                                    Id
                                }
                            }
                        }
                    }
                    fragment lightAccounts on ArrayOfCompteLightModel {
                        CompteLightModel {
                            Id
                            Libelle
                            Numero
                        }
                    }
                `,
                variables: {
                    listeIdsActivite: {decimal: activitiesList},
                }
            }).pipe(
                tap(({
                         InitialiserPopupTropPercu: {
                             ListeMoyensPaiement,
                             ListeComptes,
                             ListeJournaux,
                             ListeComptesCollectifPMV,
                             ListeComptesCollectifClient,
                             ListeBanques,
                             IdCompteBancaireActivite,
                             IdCompteTropPercu,
                             IdCollectifTropPercu
                         },
                         GetListeCompteBancaireGenerique
                     }) => {
    
                    this.loading = false;
    
                    this.paymentMethodsList = get(ListeMoyensPaiement, 'MoyensPaiementMoyenPaiementLightModel', []);
                    this.accountsList = get(ListeComptes, 'CompteLightModel', []);
                    this.journalsList = get(ListeJournaux, 'JournalLightModel', []);
                    this.bankAccountsList = GetListeCompteBancaireGenerique?.CompteBancaireDTO || [];
                    this.collectivePMVAccountsList = get(ListeComptesCollectifPMV, 'CompteLightModel', []);
                    this.overpaymentAccountsList = get(ListeComptesCollectifClient, 'CompteLightModel', []);
                    this.bankList = get(ListeBanques, 'string', []);
    
                    this.form.patchValue({
                        overpaymentBankAccount: IdCollectifTropPercu,
                        extraordinaryProduct: {
                            account: IdCompteTropPercu || head(this.accounts)?.Id || null,
                            journalExtra: get(head(this.productJournals), 'Id', null)
                        },
                        refund: {
                            bankAccountId: IdCompteBancaireActivite || get(head(this.bankAccountsList), 'Id', null),
                            paymentMethodId: get(head(this.paymentMethodsList), 'Id', null),
                            collectiveAccountId: get(head(this.collectivePMVAccountsList), 'Id', null),
                        }
                    });
    
                    this.cdr.markForCheck();
                }),
                switchMap(({InitialiserPopupTropPercu: {IdCompteTropPercu, IdSectionAnalytique}}) =>
                    this.dispatch$(new InitAnalyticalDistributionAction({
                        accountId: IdCompteTropPercu,
                        overpaymentAmount: this.overpaymentAmount,
                        analyticalSectionId: IdSectionAnalytique
                    }))
                ),
                tap(() => {
                    this.loading = false;
                    this.cdr.markForCheck();
                }),
                catchError(err => {
                    this.loading = false;
                    this.close();
                    return throwError(err);
                })
            ))
        );
    }

    /**
     * Récupère l'identifiant du journal correspondant au type de budget
     * du compte bancaire sélectionné s'il n'en existe qu'un
     * @param bankAccountId Identifiant du compte bancaire
     */
    private getJournalIdFromBankAccountBudget(bankAccountId: number): number {
        const bankAccount = this.bankAccountsList.find(b => b.Id === bankAccountId);
        if (!bankAccount) {
            return null;
        }

        const firstJournalSelector = (budgetType: 'S' | 'F'): number => {
            const filteredJournals = this.refundJournals.filter(j => j.TypeBudget === budgetType);
            if (filteredJournals.length === 1) {
                return filteredJournals[0].Id;
            }
        };

        if (bankAccount.IsSocial) {
            return firstJournalSelector('S');
        } else if (bankAccount.IsFonctionnement) {
            return firstJournalSelector('F');
        }

        return null;
    }

    private exceptionalProductLabel(): string {
        const {Prenom, Nom, CodeMembre} = this.data.beneficiary || {};
        const beneficiary = BeneficiaryPipe.format(Prenom, Nom, CodeMembre);

        const invoices = this.data.invoices.map(i => i.NumFacture);
        const [les, plural] = arrayOnlyOne(invoices) ? ['la', ''] : ['les', 's'];
        return `Produit exceptionnel - ${beneficiary} sur ${les} facture${plural} ${invoices.join('/')}`;
    }

    private generatePayment(): Observable<ReglementsReglementModelInput> {
        const {CurrentCaisse} = this.context;
        const {
            overpaymentBankAccount,
            refund,
            extraordinaryProduct
        } = this.form.value;

        const benefAccountId = get(this.snapshot(SharedPaymentMethodsState.third), ['CompteTiers', 'Id'], 0);
        const {REMB_TROP_PERCU, PRODUIT_EXCEPTIONNEL} = environment.constants.ecriture;

        const [IdJournal, writeType, Libelle, classTypeName] = this.isRefund
            ? [
                refund.journalRefund,
                REMB_TROP_PERCU,
                'Remboursement de trop-perçu',
                AccountingWritingClassType.refund
            ]
            : [
                extraordinaryProduct.journalExtra,
                PRODUIT_EXCEPTIONNEL,
                this.exceptionalProductLabel(),
                AccountingWritingClassType.extraordinaryProduct
            ];

        const writePayment: EcritureGenerationEcritureReglementModelInput & { __typename: string } = {
            __typename: classTypeName,
            IdCaisse: CurrentCaisse,
            IdUtilisateur: this.context.CurrentUserId,
            IdBeneficiaire: this.beneficiaryId,
            DateEcriture: getISODate(new KalDate()),
            IdJournal,
            Libelle,
            IdMoyenPaiement: refund?.paymentMethodId || null,
            TypeEcriture: {Key: writeType}
        };
        const mouvementList = [];

        const commonChargeProperties = {Montant: this.overpaymentAmount, Sens: accountingMovementDirection.Credit};
        const debitProperties = {
            IdCompte: overpaymentBankAccount,
            IdCompteTiers: benefAccountId,
            Montant: this.overpaymentAmount,
            Sens: accountingMovementDirection.Debit
        };

        // Si le moyen de remboursement n'est pas cheque le mouvement de débit est identique pour tous les cas
        if (this.shouldDisplayCheque) {
            mouvementList.push({
                ...debitProperties,
                NomBanque: refund.bankName,
                NumeroCheque: refund.chequeNumber,
                NomTire: refund.tiretName
            });
        } else {
            mouvementList.push(debitProperties);
        }

        if (this.isRefund) {
            if (this.shouldDisplayBankAccount) {
                const IdCompte = this.bankAccountsList.find(e => e.Id === refund.bankAccountId)?.Compte?.Id || null;
                mouvementList.unshift({
                    IdCompte,
                    ...commonChargeProperties
                });
            } else if (this.shouldDisplayPmvAccount) {
                mouvementList.unshift({
                    IdCompte: refund.collectiveAccountId,
                    IdCompteTiers: benefAccountId,
                });
            }

            if (this.selectedPaymentMethod.IsFiduciaire) {
                return this.generateFiduciaryPayment({
                    CurrentCaisse,
                    paymentMethodId: refund.paymentMethodId,
                    commonChargeProperties,
                    mouvementList,
                    writePayment
                });
            } else {
                return of({
                    Ecriture: writePayment,
                    ListeMouvements: {anyType: mouvementList}
                });
            }
        } else {
            // Produit extraordinaire
            return of({
                Ecriture: writePayment,
                ListeMouvements: {
                    anyType: [
                        {
                            __typename: 'EnregistrementProduitExceptionnelModelInput',
                            IdCompte: extraordinaryProduct.account,
                            ...commonChargeProperties,
                            ListeRepartitionAnalytique: {
                                ArrayOfanyTypeInput: generateDistributionKeys(extraordinaryProduct.distribution)
                            }
                        },
                        ...mouvementList
                    ]
                }
            });
        }
    }

    private getFiduciaryPaymentAccountId(deskId, paymentMethodId): Observable<number> {
        return this.graphqlService.mutateOne<'GetIdCompteReglementFiduciaire', MutationGetIdCompteReglementFiduciaireArgs>({
            mutation: gql`
                mutation GetIdCompteReglementFiduciaire($contexte: ContextInfosInput, $idCaisse: Float, $idMoyenPaiement: Float) {
                    GetIdCompteReglementFiduciaire(contexte: $contexte, idCaisse: $idCaisse, idMoyenPaiement: $idMoyenPaiement)
                }
            `,
            variables: {
                idCaisse: deskId,
                idMoyenPaiement: paymentMethodId,
            }
        });
    }

    private generateFiduciaryPayment({
                                         writePayment,
                                         CurrentCaisse,
                                         paymentMethodId,
                                         mouvementList,
                                         commonChargeProperties
                                     }: {
        writePayment: EcritureGenerationEcritureReglementModelInput,
        CurrentCaisse: number,
        paymentMethodId: number,
        mouvementList: any[],
        commonChargeProperties: { Montant: number, Sens: string }
    }): Observable<ReglementsReglementModelInput> {
        return this.getFiduciaryPaymentAccountId(CurrentCaisse, paymentMethodId).pipe(
            map(fiduciaryAccountId => {
                return {
                    Ecriture: writePayment,
                    ListeMouvements: {
                        anyType: [
                            {
                                IdCompte: fiduciaryAccountId,
                                ...commonChargeProperties
                            },
                            ...mouvementList
                        ]
                    }
                };
            })
        );
    }

    private activityIdsList$(): Observable<number[]> {
        return this.graphqlService.mutateOne({
          mutation: gql`
            mutation GetListeActivitesLight($contexte: ContextInfosInput) {
              GetListeActivitesLight(contexte: $contexte, Criteres: { IsClos: false }) {
                ActiviteLightModel {
                  Id               
                }
              }
            }
          `
        }).pipe(
            map(GetListeActivitesLight => GetListeActivitesLight?.ActiviteLightModel?.map(a => a.Id) || []),
        );
    }

    ngOnInit(): void {
        this.loading = false;
        this.initForm();
        this.initOverpaymentInfos();
    }
}
